/* Copyright 2014+, Federico Zivolo, LICENSE at https://github.com/FezVrasta/bootstrap-material-design/blob/master/LICENSE.md */

import { ICommonKeyValues } from '../@types/core';

export const setRipples = ($: JQueryStatic) => {
	/**
	 * Define the name of the plugin
	 */
	const ripples = 'ripples';

	/**
	 * Define the defaults of the plugin
	 */
	const defaults = {};

	/**
	 * Create the main plugin function
	 */
	class Ripples {
		element: JQuery<HTMLElement>;
		options: ICommonKeyValues;
		defaults: ICommonKeyValues;
		name: string;

		constructor (element: JQuery<HTMLElement>, options: ICommonKeyValues) {
			this.element = $(element);

			this.options = $.extend({}, defaults, options);

			this.defaults = defaults;
			this.name = ripples;

			this.init();
		}

		init () {
			const $element = this.element;

			$element.on('mousedown touchstart', (event: JQuery.TriggeredEvent) => {
				/**
				 * Verify if the user is just touching on a device and return if so
				 */
				if (this.isTouch() && event.type === 'mousedown') {
					return;
				}

				/**
				 * Verify if the current element already has a ripple wrapper element and
				 * creates if it doesn't
				 */
				if (!($element.find('.ripple-container').length)) {
					$element.append('<div class=\'ripple-container\'></div>');
				}

				/**
				 * Find the ripple wrapper
				 */
				const $wrapper = $element.children('.ripple-container');

				/**
				 * Get relY and relX positions
				 */
				const relY = this.getRelY($wrapper, event);
				const relX = this.getRelX($wrapper, event);

				/**
				 * If relY and/or relX are false, return the event
				 */
				if (!relY && !relX) {
					return;
				}

				/**
				 * Get the ripple color
				 */
				const rippleColor = this.getRipplesColor($element);

				/**
				 * Create the ripple element
				 */
				const $ripple = $('<div></div>');

				$ripple
					.addClass('ripple')
					.css({
						left: relX,
						top: relY,
						'background-color': rippleColor,
					});

				/**
				 * Append the ripple to the wrapper
				 */
				$wrapper.append($ripple);

				/**
				 * Make sure the ripple has the styles applied (ugly hack but it works)
				 */
				(() => window.getComputedStyle($ripple[0]).opacity)();

				/**
				 * Turn on the ripple animation
				 */
				this.rippleOn($element, $ripple);

				/**
				 * Call the rippleEnd function when the transition 'on' ends
				 */
				setTimeout(() => {
					this.rippleEnd($ripple);
				}, 500);

				/**
				 * Detect when the user leaves the element
				 */
				$element.on('mouseup mouseleave touchend', () => {
					$ripple.data('mousedown', 'off');

					if ($ripple.data('animating') === 'off') {
						this.rippleOut($ripple);
					}
				});

			});
		}

		getNewSize ($element: JQuery<HTMLElement>, $ripple: JQuery<HTMLElement>) {
			return (Math.max($element.outerWidth()!, $element.outerHeight()!) / $ripple.outerWidth()!) * 2.5;
		}

		getRelX ($wrapper: JQuery<HTMLElement>, event: JQuery.TriggeredEvent): number | string {
			const wrapperOffset = $wrapper.offset()!;

			if (!this.isTouch()) {
				/**
				 * Get the mouse position relative to the ripple wrapper
				 */
				return event.pageX! - wrapperOffset.left;
			} else {
				/**
				 * Make sure the user is using only one finger and then get the touch
				 * position relative to the ripple wrapper
				 */

				if ((event as JQuery.TouchStartEvent).originalEvent?.touches.length === 1) {
					return (event as JQuery.TouchStartEvent).originalEvent!.touches[0].pageX - wrapperOffset.left;
				}

				return '';
			}
		}

		getRelY ($wrapper: JQuery<HTMLElement>, event: JQuery.TriggeredEvent): number | string {
			const wrapperOffset = $wrapper.offset()!;

			if (!this.isTouch()) {
				/**
				 * Get the mouse position relative to the ripple wrapper
				 */
				return event.pageY! - wrapperOffset.top;
			} else {
				/**
				 * Make sure the user is using only one finger and then get the touch
				 * position relative to the ripple wrapper
				 */

				if ((event as JQuery.TouchStartEvent).originalEvent?.touches.length === 1) {
					return (event as JQuery.TouchStartEvent).originalEvent!.touches[0].pageY - wrapperOffset.top;
				}

				return '';
			}
		}

		/**
		 * Get the ripple color
		 */
		getRipplesColor ($element: JQuery<HTMLElement>) {
			const color = $element.data('rippleColor') ? $element.data('rippleColor') : window.getComputedStyle($element[0]).color;

			return color;
		}

		/**
		 * Verify if the client browser has transistion support
		 */
		hasTransitionSupport () {
			const thisBody = document.body || document.documentElement;
			const thisStyle = thisBody.style;

			return (
				thisStyle.transition !== undefined ||
				thisStyle.webkitTransition !== undefined
			);
		}


		/**
		 * Verify if the client is using a mobile device
		 */
		isTouch () {
			return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
		}

		/**
		 * End the animation of the ripple
		 */
		rippleEnd ($ripple: JQuery<HTMLElement>) {
			$ripple.data('animating', 'off');

			if ($ripple.data('mousedown') === 'off') {
				this.rippleOut($ripple);
			}
		}

		/**
		 * Turn off the ripple effect
		 */
		rippleOut ($ripple: JQuery<HTMLElement>) {
			$ripple.off();

			if (this.hasTransitionSupport()) {
				$ripple.addClass('ripple-out');
			} else {
				$ripple.animate({ opacity: 0 }, 100, () => {
					$ripple.trigger('transitionend');
				});
			}

			$ripple.on('transitionend webkitTransitionEnd oTransitionEnd MSTransitionEnd', () => {
				$ripple.remove();
			});
		}

		/**
		 * Turn on the ripple effect
		 */
		rippleOn ($element: JQuery<HTMLElement>, $ripple: JQuery<HTMLElement>) {
			const size = this.getNewSize($element, $ripple);

			if (this.hasTransitionSupport()) {
				$ripple
					.css({
						'-ms-transform': 'scale(' + size + ')',
						'-moz-transform': 'scale(' + size + ')',
						'-webkit-transform': 'scale(' + size + ')',
						transform: 'scale(' + size + ')',
					})
					.addClass('ripple-on')
					.data('animating', 'on')
					.data('mousedown', 'on');
			} else {
				$ripple.animate({
					width: Math.max($element.outerWidth()!, $element.outerHeight()!) * 2,
					height: Math.max($element.outerWidth()!, $element.outerHeight()!) * 2,
					'margin-left': Math.max($element.outerWidth()!, $element.outerHeight()!) * (-1),
					'margin-top': Math.max($element.outerWidth()!, $element.outerHeight()!) * (-1),
					opacity: 0.2,
				}, 500, () => {
					$ripple.trigger('transitionend');
				});
			}
		}

	}

	/**
	 * Create the jquery plugin function
	 */
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore next-line
	$.fn.ripples = function (options: ICommonKeyValues) {
		return this.each(() => {
			if (!$.data(this, 'plugin_' + ripples)) {
				$.data(this, 'plugin_' + ripples, (new Ripples(this, options)));
			}
		});
	};
};
